// Thin wrapper over supabase.auth for the UI layer. Centralises:
//   • "Sign in with Google / Apple" flows (OAuth with PKCE redirect)
//   • Email/password sign-in (kept for the demo user)
//   • Sign out
//   • Subscribe to the auth state (so AppShell can react to sign-in/sign-out)
//
// Each function returns { ok, error } — UI code maps error messages into a notice.

import { supabase, authRedirectTo } from "./supabase";

export type Provider = "google" | "apple";

export type AuthResult<T = void> =
  | { ok: true;  data: T }
  | { ok: false; error: string };

export async function signInWithPassword(
  email: string,
  password: string,
): Promise<AuthResult> {
  const { error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) return { ok: false, error: zhAuthError(error.message) };
  return { ok: true, data: undefined };
}

export async function signUpWithPassword(args: {
  email: string;
  password: string;
  displayName: string;
  /** Required: a valid invitation code. Consumed atomically by
   *  the handle_new_user() trigger on auth.users. The signup uses
   *  the signup_with_invitation RPC (04967) which bypasses
   *  GoTrue's signup endpoint — the GoTrue server on the linked
   *  project has a behaviour change that silently strips
   *  non-whitelisted keys from the data field, breaking the
   *  founder-path trigger. */
  invitationCode: string;
}): Promise<AuthResult> {
  // 1. Call signup_with_invitation RPC. This inserts the auth.users
  //    row directly with raw_user_meta_data.invitation_code set,
  //    so the handle_new_user founder-path trigger sees the code
  //    and runs to completion. The user + profile + family are
  //    created in a single transaction.
  const { data: rpcRows, error: rpcError } = await supabase.rpc(
    // Cast: the generated db.ts doesn't include RPCs added in
    // the same CI run as the call site. The RPC is real and
    // functional; the type drift is regenerated on the next
    // `supabase gen types typescript --linked --schema public`.
    "signup_with_invitation" as never,
    {
      p_email: args.email,
      p_password: args.password,
      p_display_name: args.displayName,
      p_invitation_code: args.invitationCode,
    } as never
  );
  if (rpcError) {
    return { ok: false, error: zhAuthError(rpcError.message) };
  }
  // The RPC returns a rowset with (user_id, family_id). The user
  // is committed at this point but no session exists yet (we
  // bypassed GoTrue's signUp, which would have issued one).
  // 2. Sign in with password to establish a session. signInWithPassword
  //    uses the GoTrue password grant — the same code path the user
  //    would have hit anyway — and returns a session with
  //    access_token + refresh_token.
  const { error: signInError } = await supabase.auth.signInWithPassword({
    email: args.email,
    password: args.password,
  });
  if (signInError) {
    return { ok: false, error: zhAuthError(signInError.message) };
  }
  return { ok: true, data: undefined };
}

export async function signInWithOAuth(
  provider: Provider,
): Promise<AuthResult> {
  const { error } = await supabase.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: authRedirectTo,
      // queryParams lets us hint the provider about the locale.
      queryParams: provider === "google"
        ? { hl: "zh-HK" }
        : { locale: "zh_HK" },
    },
  });
  if (error) return { ok: false, error: zhAuthError(error.message) };
  return { ok: true, data: undefined };
}

export async function signOut(): Promise<AuthResult> {
  const { error } = await supabase.auth.signOut();
  if (error) return { ok: false, error: zhAuthError(error.message) };
  return { ok: true, data: undefined };
}

/** Returns the current session, if any. */
export async function getSession() {
  const { data, error } = await supabase.auth.getSession();
  if (error) return null;
  return data.session;
}

/** Subscribes to auth state changes. Returns an unsubscribe function. */
export function onAuthChange(cb: (signedIn: boolean) => void): () => void {
  const { data } = supabase.auth.onAuthStateChange((_event, session) => {
    cb(Boolean(session));
  });
  return () => data.subscription.unsubscribe();
}

function zhAuthError(msg: string): string {
  const m = msg.toLowerCase();
  if (m.includes("invitation code is required"))        return "請輸入邀請碼以註冊帳戶。";
  if (m.includes("invitation code") && m.includes("does not exist"))
                                                        return "邀請碼無效，請向管理員查詢。";
  if (m.includes("invitation code") && m.includes("has expired"))
                                                        return "邀請碼已過期，請向管理員查詢。";
  if (m.includes("invitation code") && m.includes("no remaining uses"))
                                                        return "邀請碼已用完，請向管理員查詢。";
  if (m.includes("invalid login credentials"))          return "電郵或密碼不正確，請再試一次。";
  if (m.includes("email not confirmed"))                return "請先到電郵確認連結，再登入。";
  if (m.includes("user already registered"))            return "這個電郵已經註冊過，請直接登入。";
  if (m.includes("password") && m.includes("at least")) return "密碼至少 8 個字元。";
  if (m.includes("rate limit"))                         return "嘗試次數太多，請稍後再試。";
  if (m.includes("signup disabled"))                    return "註冊功能暫時關閉，請聯絡支援。";
  if (m.includes("provider is not enabled"))            return "此登入方式尚未啟用，請聯絡管理員。";
  return msg;
}